import { Fragment, Suspense, useMemo } from 'react';
import { Outlet, useMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWebsiteConfig } from '@wbk/contentful/api';
import useLocalization from '@/context/localization/useLocalization';
import Seo from '../headers/seo';
import FullpageAnimatedLogo from '../ui/Loading/FullpageAnimatedLogo';
import Footer from './footer';
import Header from './header/v2/HeaderV2';
// import Header from './header';

const zoneConfigAllowlist = [
  'boulevard-world',
  'boulevard-city',
  'kingdom-arena',
  'the-venue',
  'aroya-cruises',
  'the-groves',
  'via-riyadh',
] as const;

type Props = {
  type?: LayoutType;
};

const Layout = ({ type = 'main' }: Props) => {
  const { t } = useTranslation('common');
  const { locale } = useLocalization();
  const zonesMatch = useMatch('/:lang/zones/:slug');
  const zoneMatch = useMatch('/:lang/zone/:slug');

  const zoneSlug = zonesMatch?.params.slug ?? zoneMatch?.params.slug;

  const zoneConfigSlug = useMemo(() => {
    if (!zoneSlug) return null;
    const matchedZoneSlug = zoneConfigAllowlist.find((allowedSlug) =>
      zoneSlug.includes(allowedSlug)
    );

    return matchedZoneSlug ? `config-${matchedZoneSlug}-rs25` : null;
  }, [zoneSlug]);

  const { data: mainConfigData } = useWebsiteConfig({ lang: locale, slug: 'config' });
  const { data: zoneConfigData } = useWebsiteConfig({
    lang: locale,
    slug: zoneConfigSlug || 'config',
  });

  const data = zoneConfigData ?? mainConfigData;

  return (
    <Fragment>
      {data && (
        <Seo
          title={t('common:seo.title')}
          description={t('common:seo.description')}
          keywords={t('common:seo.keywords')}
          image={data.seo.image.url}
        />
      )}

      {data && <Header config={data} />}
      <main id='main'>
        <Suspense fallback={<FullpageAnimatedLogo />}>
          <Outlet />
        </Suspense>
      </main>
      {data && <Footer config={data} type={type} />}
    </Fragment>
  );
};

export default Layout;
